import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { maternityService } from '../services/maternityService'
import { Pregnancy, PregnancyOutcome, DeliveryMethod, DeliveryLocation, BabyRecord } from '../types/maternity'
import { calculateGestationalAge } from '../utils/dueDateCalculator'
import './MaternityOutcomeForm.css'

interface MaternityOutcomeFormProps {
  pregnancy: Pregnancy
  patientId: string
  onSuccess: (updatedPregnancy: Pregnancy) => void
  onCancel: () => void
}

export function MaternityOutcomeForm({
  pregnancy,
  patientId,
  onSuccess,
  onCancel,
}: MaternityOutcomeFormProps) {
  const queryClient = useQueryClient()
  const [outcome, setOutcome] = useState<PregnancyOutcome>('live-birth')
  const [outcomeDate, setOutcomeDate] = useState(new Date().toISOString().split('T')[0])
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod | undefined>()
  const [deliveryLocation, setDeliveryLocation] = useState<DeliveryLocation | undefined>()
  const [babies, setBabies] = useState<BabyRecord[]>([
    {
      name: '',
      gender: 'unknown',
      birthWeight: undefined,
      birthLength: undefined,
      apgarScore1: undefined,
      apgarScore5: undefined,
      birthTime: '',
    },
  ])

  const gestationalAge = calculateGestationalAge(pregnancy.lastMenstrualPeriod, new Date(outcomeDate))

  const outcomeMutation = useMutation({
    mutationFn: (data: {
      outcome: PregnancyOutcome
      outcomeDate?: string
      deliveryMethod?: DeliveryMethod
      deliveryLocation?: DeliveryLocation
      babies?: BabyRecord[]
    }) => maternityService.recordOutcome(pregnancy.pregnancyId, patientId, data),
    onSuccess: (updatedPregnancy) => {
      queryClient.invalidateQueries({ queryKey: ['pregnancies', patientId] })
      queryClient.invalidateQueries({ queryKey: ['pregnancy', pregnancy.pregnancyId] })
      queryClient.invalidateQueries({ queryKey: ['patient-summary', patientId] })
      onSuccess(updatedPregnancy)
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!outcome) {
      alert('Outcome is required')
      return
    }

    // For live births, validate baby data
    if (outcome === 'live-birth') {
      const validBabies = babies.filter(
        (baby) => baby.name || baby.birthWeight || baby.gender !== 'unknown'
      )
      if (validBabies.length === 0) {
        alert('Please provide at least basic information for the baby')
        return
      }
    }

    try {
      await outcomeMutation.mutateAsync({
        outcome,
        outcomeDate,
        deliveryMethod: outcome === 'live-birth' ? deliveryMethod : undefined,
        deliveryLocation: outcome === 'live-birth' ? deliveryLocation : undefined,
        babies: outcome === 'live-birth' ? babies : undefined,
      })
    } catch (error) {
      console.error('Error recording outcome:', error)
      alert('Failed to record outcome. Please try again.')
    }
  }

  const addBaby = () => {
    setBabies([
      ...babies,
      {
        name: '',
        gender: 'unknown',
        birthWeight: undefined,
        birthLength: undefined,
        apgarScore1: undefined,
        apgarScore5: undefined,
        birthTime: '',
      },
    ])
  }

  const removeBaby = (index: number) => {
    setBabies(babies.filter((_, i) => i !== index))
  }

  const updateBaby = (index: number, updates: Partial<BabyRecord>) => {
    setBabies(babies.map((baby, i) => (i === index ? { ...baby, ...updates } : baby)))
  }

  return (
    <form className="maternity-outcome-form" onSubmit={handleSubmit}>
      <div className="form-section">
        <h3>Outcome Information</h3>
        <div className="form-group">
          <label htmlFor="outcome">
            Outcome <span className="required">*</span>
          </label>
          <select
            id="outcome"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value as PregnancyOutcome)}
            required
          >
            <option value="live-birth">Live Birth</option>
            <option value="stillbirth">Stillbirth</option>
            <option value="miscarriage">Miscarriage</option>
            <option value="termination">Termination</option>
            <option value="ectopic">Ectopic Pregnancy</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="outcomeDate">Outcome Date</label>
          <input
            id="outcomeDate"
            type="date"
            value={outcomeDate}
            onChange={(e) => setOutcomeDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
          />
        </div>
        {outcomeDate && (
          <div className="form-group">
            <label>Gestational Age at Outcome</label>
            <div className="readonly-field">{gestationalAge.display}</div>
          </div>
        )}
      </div>

      {outcome === 'live-birth' && (
        <>
          <div className="form-section">
            <h3>Delivery Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="deliveryMethod">Delivery Method</label>
                <select
                  id="deliveryMethod"
                  value={deliveryMethod || ''}
                  onChange={(e) => setDeliveryMethod(e.target.value as DeliveryMethod)}
                >
                  <option value="">Not specified</option>
                  <option value="vaginal">Vaginal</option>
                  <option value="caesarean">Caesarean</option>
                  <option value="assisted">Assisted</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="deliveryLocation">Delivery Location</label>
                <select
                  id="deliveryLocation"
                  value={deliveryLocation || ''}
                  onChange={(e) => setDeliveryLocation(e.target.value as DeliveryLocation)}
                >
                  <option value="">Not specified</option>
                  <option value="hospital">Hospital</option>
                  <option value="home">Home</option>
                  <option value="birth-center">Birth Center</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="babies-header">
              <h3>Baby Details</h3>
              {babies.length < 5 && (
                <button type="button" className="btn-link" onClick={addBaby}>
                  + Add Another Baby
                </button>
              )}
            </div>
            {babies.map((baby, index) => (
              <div key={index} className="baby-form-card">
                <div className="baby-form-header">
                  <h4>Baby {index + 1}</h4>
                  {babies.length > 1 && (
                    <button
                      type="button"
                      className="btn-link danger"
                      onClick={() => removeBaby(index)}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor={`baby-name-${index}`}>Name</label>
                    <input
                      id={`baby-name-${index}`}
                      type="text"
                      value={baby.name || ''}
                      onChange={(e) => updateBaby(index, { name: e.target.value })}
                      placeholder="Baby's name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor={`baby-gender-${index}`}>Gender</label>
                    <select
                      id={`baby-gender-${index}`}
                      value={baby.gender || 'unknown'}
                      onChange={(e) => updateBaby(index, { gender: e.target.value as any })}
                    >
                      <option value="unknown">Unknown</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor={`baby-weight-${index}`}>Birth Weight (kg)</label>
                    <input
                      id={`baby-weight-${index}`}
                      type="number"
                      step="0.01"
                      min="0"
                      value={baby.birthWeight || ''}
                      onChange={(e) =>
                        updateBaby(index, {
                          birthWeight: e.target.value ? parseFloat(e.target.value) : undefined,
                        })
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor={`baby-length-${index}`}>Birth Length (cm)</label>
                    <input
                      id={`baby-length-${index}`}
                      type="number"
                      step="0.1"
                      min="0"
                      value={baby.birthLength || ''}
                      onChange={(e) =>
                        updateBaby(index, {
                          birthLength: e.target.value ? parseFloat(e.target.value) : undefined,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor={`baby-apgar1-${index}`}>Apgar Score (1 min)</label>
                    <input
                      id={`baby-apgar1-${index}`}
                      type="number"
                      min="0"
                      max="10"
                      value={baby.apgarScore1 || ''}
                      onChange={(e) =>
                        updateBaby(index, {
                          apgarScore1: e.target.value ? parseInt(e.target.value) : undefined,
                        })
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor={`baby-apgar5-${index}`}>Apgar Score (5 min)</label>
                    <input
                      id={`baby-apgar5-${index}`}
                      type="number"
                      min="0"
                      max="10"
                      value={baby.apgarScore5 || ''}
                      onChange={(e) =>
                        updateBaby(index, {
                          apgarScore5: e.target.value ? parseInt(e.target.value) : undefined,
                        })
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor={`baby-time-${index}`}>Birth Time</label>
                    <input
                      id={`baby-time-${index}`}
                      type="time"
                      value={baby.birthTime || ''}
                      onChange={(e) => updateBaby(index, { birthTime: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={outcomeMutation.isPending}
        >
          {outcomeMutation.isPending ? 'Recording...' : 'Record Outcome'}
        </button>
      </div>
    </form>
  )
}
